import React, { useEffect, useState } from 'react'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import { Typography, Box, Button, Link } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SimpleModal from '../Other/SimpleModal'
import InfoIcon from '@material-ui/icons/Info'
import { TitleMedium } from '../../theme/typography'
import PropTypes from 'proptypes'
import { withStyles } from '@material-ui/core/styles'
import styled from 'styled-components'
import shadows from '../../theme/shadows'

const Accordion = withStyles((theme) => ({
  root: {
    backdropFilter: 'blur(30px)',
    backgroundColor: (props) => (props.bgColor ? props.bgColor : theme.colors.white),
    boxShadow: shadows[7],
    margin: theme.spacing(1, 0),
    borderRadius: 8,
    transition: ' transform .5s',
    backfaceVisibility: 'hidden',
    WebkitFontSmoothing: 'subpixel-antialiased',
    transform: 'perspective(1px) translateZ(0)',

    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(2, 5)
    },

    '&:not(:last-child)': {
      borderBottom: 0
    },
    '&:before': {
      display: 'none'
    },
    '&$expanded': {
      boxShadow: shadows[4],
      zIndex: 1,
      transform: 'perspective(1px) translateZ(0)',
      margin: theme.spacing(1, 0),
      [theme.breakpoints.up('sm')]: {
        margin: theme.spacing(3.5)
      }
    }
  },
  expanded: {}
}))(MuiAccordion)

const AccordionSummary = withStyles(() => ({
  root: {
    marginBottom: -1,
    minHeight: 60,
    '&$expanded': {
      minHeight: 60
    }
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    '&$expanded': {
      margin: '12px 0'
    }
  },
  expandIcon: {
    '& svg': {
      fontSize: '2rem'
    }
  },
  expanded: {
    '& h3': {
      fontSize: '1.5rem',
      fontWeight: 'bold'
    }
  }
}))(MuiAccordionSummary)

const AccordionDetails = withStyles((theme) => ({
  root: {
    borderTop: `1px solid ${theme.colors.grey2}`,
    display: 'block',
    padding: theme.spacing(2),
    textAlign: 'center'
  }
}))(MuiAccordionDetails)

const HelpfulBox = withStyles((theme) => ({
  root: {
    margin: theme.spacing(4, -2, -2, -2),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.colors.grey2}`,
    '& a': {
      alignItems: 'center',
      display: 'flex',
      '& svg': {
        width: 20,
        height: 20,
        fill: theme.colors.grey9,
        marginRight: theme.spacing(1)
      }
    }
  }
}))(Box)

const PriceButton = withStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 1),
    marginTop: 'auto',
    height: 36,
    '& .text--crossout': {
      textDecoration: 'line-through',
      opacity: 0.65
    }
  },
  label: {
    flexWrap: 'wrap',
    lineHeight: 1.1,
    '& span': {
      padding: theme.spacing(0, 0.2)
    }
  },
  containedSecondary: {
    backgroundColor: theme.colors.pink,
    backgroundImage: 'linear-gradient(105deg, rgb(255, 101, 132), rgb(255, 101, 132))',
    '&:hover, &:focus': {
      backgroundColor: theme.colors.pink,
      backgroundImage: 'linear-gradient(105deg, rgb(255, 101, 132), rgb(255, 76, 112))'
    }
  }
}))(Button)

const IconWrap = styled(Box)(() => ({
  width: 24,
  height: 24,
  marginRight: '1rem'
}))
const FlagWrapWrap = styled(Box)(() => ({
  marginRight: '1rem',
  borderRadius: '50%',
  border: '1px solid rgba(13, 49, 63, 0.1)'
}))
const FlagWrap = styled(Box)(({ small }) => ({
  position: 'relative',
  width: small ? 22 : 24,
  height: small ? 22 : 24,
  overflow: 'hidden',
  borderRadius: '50%'
}))
const Flag = styled('img')(() => ({
  width: 'auto',
  height: '100%',
  display: 'block',
  position: 'absolute',
  maxWidth: 'none',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
}))

const PromoBadge = styled(Box)(({ theme, large }) => ({
  position: 'relative',
  marginLeft: theme.spacing(2),
  width: large ? 56 : 36,
  height: large ? 56 : 36,
  flexBasis: large ? 56 : 36,
  lineHeight: 1.1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  boxShadow: shadows[2],
  backgroundColor: theme.colors.pink,
  color: theme.colors.white,
  borderRadius: '50%',
  transform: 'rotate(20deg)',
  zIndex: 1,
  fontSize: large ? 16 : 13,
  flexShrink: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 4,
    left: 4,
    border: `1px dashed ${theme.colors.white}`,
    width: large ? 48 : 28,
    height: large ? 48 : 28,
    borderRadius: '50%'
  }
}))

const BadgeWrap = styled(Box)(({ large }) => ({
  position: 'absolute',
  top: large ? '-.5rem ' : '-.3rem',
  right: large ? '-.5rem ' : '-.3rem'
}))
const Duration = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2) + 'px !important'
}))
const AllowanceBox = styled(Box)(() => ({
  '& h4': {
    fontWeight: 'bold',
    fontSize: '1rem',
    marginLeft: 3
  },
  '& h3': {
    fontWeight: 'bold',
    fontSize: '1.5rem',
    lineHeight: 1.23,
    letterSpacing: -2
  }
}))
const PlanBox = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  margin: '.5%',
  width: '100%',
  borderRadius: 8,
  border: 'solid 1px rgba(13, 49, 63, 0.2)',
  padding: '20px 3% 10px',
  '@media (min-width: 350px)': {
    width: '49%'
  },
  '@media (min-width: 520px)': {
    width: '32%',
    padding: '20px 10px 10px'
  },
  '@media (min-width: 768px)': {
    flex: '0 0 19%',
    maxWidth: 150
  }
}))

const CoverageTitle = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(2) + 'px !important'
}))
const Country = styled(Box)(({ selected }) => ({
  position: 'relative',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  padding: '8px 5px',
  marginBottom: 0,
  breakInside: 'avoid-column',
  display: 'flex',
  alignItems: 'center',
  lineHeight: 1.8,
  borderRadius: 4,
  backgroundColor: selected ? 'rgba(41, 157, 193, 0.05)' : 'transparent',
  '&::after': {
    content: selected ? '""' : '',
    position: 'absolute',
    top: '3%',
    left: 0,
    width: '100%',
    height: '96%',
    border: 'solid 1.5px #299dc1',
    borderRadius: 4
  }
}))
const CountriesList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: 0,
  paddingTop: theme.spacing(5),
  borderTop: `1px solid ${theme.colors.grey2}`,
  columnCount: 1,
  '@media (min-width: 350px)': {
    columnCount: 2,
    columnGap: 4
  },
  '@media (min-width: 768px)': {
    columnCount: 3
  },
  '@media (min-width: 960px)': {
    columnCount: 4
  }
}))

const ExtraData = styled(Box)(() => ({
  fontSize: 14,
  fontWeight: 'bold',
  color: '#fff',
  background: '#ff6584',
  borderRadius: '8px',
  margin: '3px auto',
  padding: '2px 16px'
}))

function PlanAllowance(props) {
  const [price, setPrice] = useState()
  const [previousPrice, setPreviousPrice] = useState()
  const [percentage, setPercentage] = useState()

  useEffect(() => {
    if (props.isFree) {
      setPrice(props.price)
      return
    }

    let currency = props.selectedCurrency
    let allowancePrice = props.prices.find((x) => x.currency === props.selectedCurrency)?.price

    if (!allowancePrice) {
      allowancePrice = props.prices[0].price
      currency = props.prices[0].currency
    }

    allowancePrice = Number(allowancePrice)
    setPrice(
      allowancePrice.toLocaleString(navigator.language || 'en-GB', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: allowancePrice % 1 > 0 ? 2 : 0
      })
    )
  }, [props.selectedCurrency])

  return (
    <PlanBox>
      <AllowanceBox display="flex" alignItems="flex-end" justifyContent="center">
        <Typography component="h3">{props.allowance}</Typography>
        <Typography component="h4">{props.unit}</Typography>
      </AllowanceBox>

      <Typography variant="subtitle2" color="textSecondary">
        {props.dataText}
      </Typography>

      {props.extraData && <ExtraData>{props.extraData}</ExtraData>}

      <Duration variant="subtitle1" color="textSecondary">
        {props.duration}
      </Duration>

      <SimpleModal isThin="true" {...props.modal}>
        <PriceButton
          style={{ backgroundColor: '#2a9ec2' }}
          variant="contained"
          size="small"
          color={previousPrice ? 'secondary' : 'primary'}
        >
          {previousPrice && <span className="text--crossout">{previousPrice}</span>}
          <span>{price}</span>
        </PriceButton>
      </SimpleModal>

      {previousPrice && (
        <BadgeWrap large>
          <PromoBadge large>{percentage}%</PromoBadge>
        </BadgeWrap>
      )}
    </PlanBox>
  )
}

function PlansAccordion(props) {
  const hasPromotionalPrice = props.allowances.find(
    (allowance) =>
      (allowance.previousPrices != null && Object.keys(allowance.previousPrices).length > 0) ||
      (allowance.previousPrice != null && !isNaN(allowance.previousPrice))
  )

  return (
    <Accordion square expanded={props.isOpen} ref={props.refer}>
      <AccordionSummary
        onClick={() => props.toggleOpen(props.name)}
        expandIcon={
          props.allowOpenClose && props.isOpen ? (
            <ExpandMoreIcon />
          ) : props.allowOpenClose ? (
            <ExpandMoreIcon />
          ) : null
        }
        aria-controls={`country-content`}
        id={`country-header`}
        style={{ cursor: props.allowOpenClose ? 'pointer' : 'default' }}
      >
        {props.iso ? (
          <FlagWrapWrap>
            <FlagWrap>
              <Flag
                src={`../../../images/flags/${props.iso.toLowerCase()}.svg`}
                loading="lazy"
                alt={props.iso}
              />
            </FlagWrap>
          </FlagWrapWrap>
        ) : (
          props.iconName && (
            <IconWrap>
              <img
                src={`../../../images/icons/${props.iconName.toLowerCase()}.svg`}
                loading="lazy"
                alt={props.name}
              />
            </IconWrap>
          )
        )}
        <TitleMedium variant="h3">{props.name}</TitleMedium>
        {props.coverageTitle && !props.isOpen && props.footprintCountries.length > 1 && (
          <CoverageTitle
            variant="h5"
            color="textSecondary"
            dangerouslySetInnerHTML={{
              __html: props.coverageTitle.replace('{count}', props.footprintCountries.length)
            }}
          />
        )}
        {hasPromotionalPrice && <PromoBadge>%</PromoBadge>}
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" justifyContent="center" flexWrap="wrap">
          {props.freePlanEnabled && (
            <PlanAllowance
              key="allowance-free-trial"
              allowance={props.freePlanAllowance}
              unit={props.freePlanUnit}
              duration={props.freePlanDuration}
              price={props.freePlanPrice}
              isFree={true}
            />
          )}
          {props.allowances &&
            props.allowances.map((allowance, i) => (
              <PlanAllowance
                key={`allowance-${i}`}
                dataText={props.dataText}
                selectedCurrency={props.selectedCurrency}
                modal={props.modal}
                {...allowance}
              />
            ))}
        </Box>

        {props.footprintCountries && props.footprintCountries.length > 1 && (
          <>
            {props.coverageDescription && (
              <Box mt={4} mb={2}>
                <TitleMedium
                  variant="h3"
                  dangerouslySetInnerHTML={{
                    __html: props.coverageDescription.replace(
                      '{count}',
                      props.footprintCountries.length
                    )
                  }}
                />
              </Box>
            )}
            <CountriesList component="ul">
              {props.footprintCountries.map((country) => {
                return (
                  <Country
                    key={`country-${country.iso}`}
                    component="li"
                    selected={
                      props.selectedCountries ? props.selectedCountries.includes(country) : false
                    }
                  >
                    <FlagWrapWrap>
                      <FlagWrap small>
                        <Flag
                          src={`../../../images/flags/${country.iso.toLowerCase()}.svg`}
                          alt={country.iso}
                        />
                      </FlagWrap>
                    </FlagWrapWrap>
                    {country.name}
                  </Country>
                )
              })}
            </CountriesList>
          </>
        )}
        {props.howMuchDataLink && (
          <HelpfulBox display="flex">
            <Link
              display="flex"
              href={props.howMuchDataLink.href}
              title={props.howMuchDataLink.title}
              target={props.howMuchDataLink.openIn}
              underline="none"
            >
              <InfoIcon />
              <Typography variant="subtitle1">{props.howMuchDataLink.text}</Typography>
            </Link>
          </HelpfulBox>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default PlansAccordion

PlansAccordion.propTypes = {
  list: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string
    })
  )
}
